// locationsApi — typed fetchers for /v1/locations/* (Route 3).
import { apiFetch } from "../../lib/api-client.js";

export type LocationStatus = "active" | "retired";
export type LocationRisk = "low" | "medium" | "high";

export interface LocationRow {
  id: string;
  tenant_id: string;
  site_id: string;
  site_name: string | null;
  code: string;
  label: string;
  location_hint: string | null;
  status: LocationStatus;
  risk_level: LocationRisk;
  /** Geofence (Phase 2 PR H). Null until admin pins a location on the map. */
  lat: number | null;
  lon: number | null;
  geofence_radius_m: number;
  ntag_uid: string | null;
  ntag_bound: boolean;
  created_at: string;
  updated_at: string;
}

export interface LocationListResponse {
  items: LocationRow[];
  total: number;
  correlation_id: string | null;
}

export class LocationsApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  fields?: string[];
  /** Optional server-provided message (sub-API onError surfaces real exception text here). */
  upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, fields?: string[], upstreamMessage?: string) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
    this.fields = fields;
    this.upstreamMessage = upstreamMessage;
  }
}

export function friendlyLocationsError(err: unknown): string {
  if (err instanceof LocationsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改地點設定。";
    if (err.status === 404) return "找不到這個地點（可能已被封存）。";
    if (err.status === 409 && err.code === "duplicate_code") return "這個地點代碼已被同案場的另一個地點使用，請換一個。";
    if (err.status === 400) {
      if (err.code === "tenant_id_required")
        return "尚未選定操作的單位，請先到「平台 / 租戶」選擇要進入的單位。";
      if (err.code === "site_not_found") return "選定的案場不存在或不屬於此單位。";
      if (err.code === "invalid_body") {
        const f = (err.fields ?? []).join("、");
        return `欄位填寫不完整：${f}。`;
      }
    }
    const msgPart = err.upstreamMessage ? `・msg=${err.upstreamMessage}` : "";
    return `資料暫時無法存取（HTTP ${err.status}・code=${err.code}${msgPart}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<LocationsApiError> {
  let code = "request_failed";
  let fields: string[] | undefined;
  let cid: string | null = r.headers.get("x-correlation-id");
  let upstreamMessage: string | undefined;
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[]; message?: string };
    code = body.error ?? code;
    cid = body.correlation_id ?? cid;
    fields = body.fields;
    upstreamMessage = body.message;
  } catch {}
  return new LocationsApiError(r.status, code, cid, fields, upstreamMessage);
}

export interface LocationsQuery {
  tenantId?: string | null;
  siteId?: string | null;
  q?: string | null;
  status?: LocationStatus | "" | null;
  risk?: LocationRisk | "" | null;
  limit?: number | null;
  offset?: number | null;
}

export async function listLocations(params: LocationsQuery = {}): Promise<LocationListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.siteId) qs.set("site_id", params.siteId);
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.risk) qs.set("risk", params.risk);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const r = await apiFetch(`/v1/locations${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as LocationListResponse;
}

export interface CreateLocationBody {
  site_id: string;
  code: string;
  label: string;
  location_hint?: string | null;
  risk_level?: LocationRisk;
  status?: LocationStatus;
  /** Optional geofence — admin map picker (Phase 2 PR H). */
  lat?: number | null;
  lon?: number | null;
  geofence_radius_m?: number;
}

export async function createLocation(body: CreateLocationBody, tenantId?: string | null): Promise<{ id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/locations${qs}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export type PatchLocationBody = Partial<CreateLocationBody>;
export async function patchLocation(id: string, body: PatchLocationBody, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/locations/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export async function retireLocation(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/locations/${encodeURIComponent(id)}${qs}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}
